import { filterPipelineRowsBySearch } from './filterPipelineRowsBySearch';
import { DELETED_APP_INSTANCE_MARKER, NEVER_RUN_STATUS_VALUE } from '../constants/pipelineListNarrowing';

// pipelines-list-sort-and-narrow — the narrowing predicates (AC-9..AC-13).
//
// Built as EXPLICIT CONTROLS FEEDING A PURE PREDICATE rather than as the DataGrid's own filter
// panel. Two reasons, both load-bearing: the community edition's filter panel is single-field
// (multi-column filter BUILDING is Pro, @mui/x-data-grid 5.17.11 per FE/package.json), so the
// panel could not express "app instance AND status" anyway; and a predicate is unit-testable,
// which the panel is not. This sidesteps the licence tier instead of arguing with it.
//
// Everything binds to fields ALREADY on the row (AppInstanceName, LatestRunStatus, both from
// flattenPipelineGroups) — no new payload field, no new request, so the one-round-trip guarantee
// of deploy-pipelines-list-payload AC-6 is untouched (AC-10, AC-21).

// AC-11 — a row's facet VALUE, which is its App Instance name or the literal `(deleted)` when it
// has no resolvable owner. This is the single function that decides what `(deleted)` means, and
// both the option list and the predicate go through it, so they cannot disagree.
export const getPipelineRowAppInstanceFacetValue = row => (row?.AppInstanceName == null || row.AppInstanceName === '' ? DELETED_APP_INSTANCE_MARKER : row.AppInstanceName);

// AC-11 — the facet's value list. `(deleted)` appears as a first-class selectable option whenever
// at least one row is orphaned, and is absent when none is.
//
// Sorted case-insensitively for scannability, with `(deleted)` forced LAST regardless of where
// the punctuation would put it — same reasoning as AC-5's nulls-last: it is real and reachable,
// but it is not what anyone is looking for first.
//
// ⚠ `selectedValues` EXISTS FOR ONE CASE, AND IT IS AC-16b'S OWN HEADLINE PATH (review LOW-1,
// 2026-08-05). The facet's VALUE comes from the URL; its OPTIONS come from the loaded rows. Those
// two can legitimately disagree — a narrowed link shared per AC-16b, opened after that App
// Instance's last pipeline is gone. MUI then logs "The value provided to Autocomplete is invalid.
// None of the options match with …" on a supported path, and a selection with no matching option is
// a selection the reader cannot see the shape of.
//
// So a currently-SELECTED value is always representable, even when no row carries it. The honest
// cost, stated because the earlier version of this comment promised the opposite ("never offers a
// value that would return nothing"): while such a selection is active, the dropdown does list one
// value that matches zero rows. That is strictly better than the alternatives — dropping it from
// the value would leave the filter applied with its chip invisible, so the reader would face an
// empty list with no visible cause. An option that is present, empty and REMOVABLE beats a filter
// that is invisible and stuck.
export const buildAppInstanceFacetOptions = (rows = [], selectedValues = []) => {
	const values = new Set((rows ?? []).map(getPipelineRowAppInstanceFacetValue));
	// Only non-empty selections; an empty string is never a facet value (see the URL parser).
	(selectedValues ?? []).forEach(value => {
		if (value !== null && value !== undefined && value !== '') values.add(value);
	});
	const hasDeleted = values.delete(DELETED_APP_INSTANCE_MARKER);
	const named = [...values].sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base' }));
	return hasDeleted ? [...named, DELETED_APP_INSTANCE_MARKER] : named;
};

// AC-10/AC-11 — narrow to the selected App Instance(s). An EMPTY selection means "no narrowing",
// never "match nothing": an empty facet must show the full list, which is the state the page opens
// in.
export const filterPipelineRowsByAppInstance = (rows = [], selectedAppInstances = []) => {
	const selected = selectedAppInstances ?? [];
	if (selected.length === 0) return rows ?? [];
	const wanted = new Set(selected);
	return (rows ?? []).filter(row => wanted.has(getPipelineRowAppInstanceFacetValue(row)));
};

// AC-12 — narrow by latest run status, with never-run as an explicit option.
//
// The never-run bucket is matched through the `NEVER_RUN_STATUS_VALUE` string sentinel rather
// than by looking for `null` in the selection, so a selection is always an array of comparable
// scalars — which is what makes it serialisable to the URL (AC-16) without a special case.
//
// Selected values arrive from the URL as strings, so statuses are compared via String() on both
// sides: `?status=4` must match the numeric `4` the BE sends (no JsonStringEnumConverter is
// registered — the wire value is a raw integer).
export const filterPipelineRowsByStatus = (rows = [], selectedStatuses = []) => {
	const selected = selectedStatuses ?? [];
	if (selected.length === 0) return rows ?? [];

	const wantsNeverRun = selected.some(value => String(value) === NEVER_RUN_STATUS_VALUE);
	const wantedStatuses = new Set(selected.filter(value => String(value) !== NEVER_RUN_STATUS_VALUE).map(value => String(value)));

	return (rows ?? []).filter(row => {
		const status = row?.LatestRunStatus;
		if (status == null) return wantsNeverRun;
		return wantedStatuses.has(String(status));
	});
};

// AC-13 — the three narrowings compose with AND.
//
// Search runs FIRST and is left exactly as it was: `filterPipelineRowsBySearch` is untouched,
// including its documented quirk that an App Instance NAME match keeps ALL of that group's
// pipelines. Changing that quirk is explicitly out of scope for this slice, and composing around
// it rather than reimplementing it is what guarantees it stays unchanged.
export const narrowPipelineRows = (rows = [], { searchTerm = '', appInstances = [], statuses = [] } = {}) => {
	const bySearch = filterPipelineRowsBySearch(rows ?? [], searchTerm);
	const byAppInstance = filterPipelineRowsByAppInstance(bySearch, appInstances);
	return filterPipelineRowsByStatus(byAppInstance, statuses);
};

export default narrowPipelineRows;
